import React, { useEffect, useMemo } from 'react';
import { RelationshipType, TitleCommon, TitleFull } from '@warehouse/title/core';
import { useAddChildrenMutation } from '@warehouse/title/domain';
import { getWorkTypeUuid } from '@warehouse/title/infra';
import {
	multiTitleSelectorSelector,
	MultiTitleSelectorStoreProvider,
	useMultiTitleSelectorStore,
} from '@warehouse/title-shared/multi-titles-selector-feature/core';
import { MultiTitlesSelector, TitleSearchInput } from '@warehouse/title-shared/multi-titles-selector-feature/ui';

export function AddChildrenPrompt(props: AddChildrenPromptProps) {
	return (
		<MultiTitleSelectorStoreProvider>
			<AddChildrenPromptWithStoreProvided {...props} />
		</MultiTitleSelectorStoreProvider>
	);
}

function AddChildrenPromptWithStoreProvided({
	relationshipType,
	searchAndSelectedPluralLabel,
	open,
	onClose,
	showHiddenTitles,
	textHeader,
	title,
}: AddChildrenPromptProps) {
	const paginationStore = useMultiTitleSelectorStore((state) => state);
	const selectableTitles = useMultiTitleSelectorStore(multiTitleSelectorSelector.selectableTitles);
	const isPending = useMultiTitleSelectorStore(multiTitleSelectorSelector.isPending);
	const actions = useMultiTitleSelectorStore(multiTitleSelectorSelector.actions);
	const showHiddenTitlesFromStore = useMultiTitleSelectorStore(multiTitleSelectorSelector.showHiddenTitles);
	const licensorLabelToFilterOn = useMemo(() => title?.readonly.licensor.label, [title]);
	const licensorLabelToFilterOnFromStore = useMultiTitleSelectorStore(
		multiTitleSelectorSelector.licensorLabelToFilterOn,
	);
	const relationshipTypeFromStore = useMultiTitleSelectorStore(multiTitleSelectorSelector.relationshipType);

	const { mutate, loading } = useAddChildrenMutation({
		options: {
			onCompleted: () => {
				handleClose();
				actions.clearSelectedTitles();
			},
		},
	});

	useEffect(() => {
		if (relationshipType && relationshipTypeFromStore !== relationshipType)
			actions.setRelationshipType(relationshipType);
	}, [actions, relationshipTypeFromStore, relationshipType]);

	useEffect(() => {
		if (showHiddenTitlesFromStore !== showHiddenTitles) actions.setShowHiddenTitles(showHiddenTitles ?? false);
	}, [actions, showHiddenTitles, showHiddenTitlesFromStore]);

	useEffect(() => {
		if (licensorLabelToFilterOn && licensorLabelToFilterOnFromStore !== licensorLabelToFilterOn)
			actions.setLicensorLabelToFilterOn(licensorLabelToFilterOn);
	}, [actions, licensorLabelToFilterOn, licensorLabelToFilterOnFromStore]);

	if (!title || !title.readonly.workType) {
		return null;
	}

	const handleSubmit = (selectedTitles: TitleCommon[]) => {
		mutate({
			titleUuid: title.uuid,
			workTypeUuid: getWorkTypeUuid(title.readonly.workType),
			children: selectedTitles.map((_title) => ({
				uuid: _title.uuid,
				workType: _title.readonly.workType ?? undefined,
			})),
			relationshipType,
		});
	};

	const handleClose = () => {
		actions.setSearchValue('');
		actions.clearSelectedTitles();
		actions.setWorkTypesToFilterOn(null);
		actions.setLicensorLabelToFilterOn(null);
		actions.setShowHiddenTitles(false);
		onClose();
	};
	return (
		<MultiTitlesSelector
			isOpen={open}
			onClose={handleClose}
			selectableTitles={selectableTitles}
			textHeader={textHeader}
			searchAndSelectedPluralLabel={searchAndSelectedPluralLabel}
			paginationStore={paginationStore}
			onSubmit={handleSubmit}
			loading={isPending || loading}
			searchInputsSlot={<TitleSearchInput searchPlaceholder={`Search ${searchAndSelectedPluralLabel}`} />}
		/>
	);
}

export interface AddChildrenPromptProps {
	title: TitleFull;
	textHeader: string;
	relationshipType?: RelationshipType;
	showHiddenTitles?: boolean;
	open: boolean;
	searchAndSelectedPluralLabel: string;
	onClose: () => void;
}
